import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { fetchGscSites, fetchGscData } from '../services/gscService';
import { getUserRankings } from '../services/storageService';
import { GscSite, GscRow, TrackedKeyword, View } from '../types';
import { auth, googleProvider } from '../services/firebase';
import { 
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
    CartesianGrid, Tooltip, BarChart, Bar 
} from 'recharts';
import { 
    TrendingUp, MousePointer, Eye, Zap, MapPin, 
    Globe, RefreshCw, ArrowUpRight, ShieldCheck, 
    ChevronRight, Sparkles, AlertCircle
} from 'lucide-react';

interface SeoPerformanceSummaryProps {
    setView: (view: View) => void;
}

const SeoPerformanceSummary: React.FC<SeoPerformanceSummaryProps> = ({ setView }) => {
    const { user } = useAuth();
    const { success, error: toastError } = useToast();

    // GSC Connection & Data State
    const [gscToken, setGscToken] = useState<string | null>(null);
    const [sites, setSites] = useState<GscSite[]>([]);
    const [selectedSite, setSelectedSite] = useState<string>('');
    const [gscRows, setGscRows] = useState<GscRow[]>([]);
    const [loadingGsc, setLoadingGsc] = useState(false);
    const [dateRange, setDateRange] = useState<number>(28);
    const [metricView, setMetricView] = useState<'ranking' | 'traffic'>('ranking');

    // Tracked Rankings fallback
    const [trackedKeywords, setTrackedKeywords] = useState<TrackedKeyword[]>([]);

    // Load initial data
    useEffect(() => {
        if (!user) return;
        loadLocalRankings();
    }, [user]);

    const loadLocalRankings = async () => {
        if (!user) return;
        try {
            const rankings = await getUserRankings(user.uid);
            setTrackedKeywords(rankings || []);
        } catch (e) {
            console.warn("Could not load local rankings:", e);
        }
    };

    const handleConnectGsc = async () => {
        try {
            const result = await auth.signInWithPopup(googleProvider);
            const credential = result.credential as any;
            if (credential && credential.accessToken) {
                setGscToken(credential.accessToken);
                success("Kết nối GSC thành công!", "Đang đồng bộ dữ liệu Search Console...");
                loadSites(credential.accessToken);
            } else {
                toastError("Không lấy được OAuth Access Token.");
            }
        } catch (e: any) {
            toastError("Lỗi kết nối Search Console: " + (e.message || "Huỷ popup"));
        }
    };

    const loadSites = async (token: string) => {
        setLoadingGsc(true);
        try {
            const siteList = await fetchGscSites(token);
            const verified = (siteList || []).filter(s => s.permissionLevel !== 'siteRestrictedUser');
            setSites(verified);
            if (verified.length > 0) {
                const initialSite = verified[0].siteUrl;
                setSelectedSite(initialSite);
                fetchSitePerformance(token, initialSite, dateRange);
            }
        } catch (e) {
            toastError("Không thể tải danh sách website từ GSC.");
        } finally {
            setLoadingGsc(false);
        }
    };

    const fetchSitePerformance = async (token: string, siteUrl: string, days: number) => {
        if (!token || !siteUrl) return;
        setLoadingGsc(true);
        try {
            const endDate = new Date().toISOString().slice(0, 10);
            const startObj = new Date();
            startObj.setDate(startObj.getDate() - days);
            const startDate = startObj.toISOString().slice(0, 10);

            const rows = await fetchGscData(token, siteUrl, startDate, endDate, ['query', 'page']);
            setGscRows(rows || []);
        } catch (e) {
            console.error("GSC Data Fetch Error:", e);
        } finally {
            setLoadingGsc(false);
        }
    };

    const handleRefresh = () => {
        if (gscToken && selectedSite) {
            fetchSitePerformance(gscToken, selectedSite, dateRange);
        } else {
            loadLocalRankings();
            success("Đã làm mới dữ liệu!");
        }
    };

    const handleSiteChange = (siteUrl: string) => {
        setSelectedSite(siteUrl);
        if (gscToken) {
            fetchSitePerformance(gscToken, siteUrl, dateRange);
        }
    };

    const handleDateRangeChange = (days: number) => {
        setDateRange(days);
        if (gscToken && selectedSite) {
            fetchSitePerformance(gscToken, selectedSite, days);
        }
    };

    // Calculate aggregated metrics
    const totalClicks = gscRows.reduce((acc, r) => acc + (r.clicks || 0), 0);
    const totalImpressions = gscRows.reduce((acc, r) => acc + (r.impressions || 0), 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgPosition = gscRows.length > 0 
        ? gscRows.reduce((acc, r) => acc + (r.position || 0), 0) / gscRows.length 
        : 0;

    // Prepare chart data for Top 10 Keywords Ranking & Traffic
    const chartData = (gscRows.length > 0 
        ? gscRows.slice(0, 10).map((r, i) => {
            const kw = r.keys?.[0] || `Keyword #${i+1}`;
            return {
                keyword: kw.length > 18 ? kw.substring(0, 16) + '...' : kw,
                fullKeyword: kw,
                position: Number(r.position.toFixed(1)),
                invertedRank: Math.max(1, 100 - r.position), // For area chart where higher is better
                clicks: r.clicks,
                impressions: r.impressions,
                ctr: Number((r.ctr * 100).toFixed(1))
            };
        })
        : trackedKeywords.slice(0, 10).map((tk, i) => {
            const kw = tk.keyword || `Keyword #${i+1}`;
            const rank = tk.currentRank > 0 ? tk.currentRank : 100;
            return {
                keyword: kw.length > 18 ? kw.substring(0, 16) + '...' : kw,
                fullKeyword: kw,
                position: rank,
                invertedRank: Math.max(1, 100 - rank),
                clicks: Math.round((tk.volume ? parseInt(String(tk.volume).replace(/\D/g, '')) || 500 : 500) * 0.15),
                impressions: tk.volume ? parseInt(String(tk.volume).replace(/\D/g, '')) || 1200 : 1200,
                ctr: 8.5
            };
        })
    );

    const hasData = chartData.length > 0;

    return (
        <div className="bg-white rounded-3xl border border-stone-200/90 shadow-sm p-6 lg:p-8 relative overflow-hidden transition-all">
            {/* Background Accent Subtle Grid */}
            <div className="absolute inset-0 opacity-[0.25] pointer-events-none bg-[radial-gradient(#e7e5e4_1px,transparent_1px)] [background-size:20px_20px] -z-0"></div>

            {/* Header / Title Row */}
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-stone-100">
                <div className="flex items-start sm:items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-brand-600 via-amber-500 to-amber-400 flex items-center justify-center text-white shadow-md shadow-amber-500/20 shrink-0">
                        <TrendingUp size={22}/>
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-xl font-extrabold text-stone-900 tracking-tight">SEO Performance Summary</h3>
                            <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full border border-blue-200/60 flex items-center gap-1">
                                <Globe size={11}/> Google Search Console
                            </span>
                            {gscToken && (
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-full border border-emerald-200/60 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Live API
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Xu hướng thứ hạng từ khóa (SERP Rankings), Clicks và độ phủ hiển thị theo thời gian thực.
                        </p>
                    </div>
                </div>

                {/* Right controls */}
                <div className="flex items-center gap-2.5 flex-wrap">
                    {/* Website Selector if connected */}
                    {gscToken && sites.length > 0 && (
                        <div className="relative">
                            <select 
                                value={selectedSite} 
                                onChange={(e) => handleSiteChange(e.target.value)}
                                className="bg-stone-50 border border-stone-200 text-stone-700 text-xs font-bold rounded-xl px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                            >
                                {sites.map(s => (
                                    <option key={s.siteUrl} value={s.siteUrl}>
                                        {s.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Metric Switcher */}
                    <div className="bg-stone-100 p-1 rounded-xl flex items-center gap-1 text-xs font-bold text-stone-600 border border-stone-200/60">
                        <button
                            onClick={() => setMetricView('ranking')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${
                                metricView === 'ranking' ? 'bg-white text-stone-900 shadow-xs' : 'hover:text-stone-900'
                            }`}
                        >
                            Thứ hạng (Rank)
                        </button>
                        <button
                            onClick={() => setMetricView('traffic')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${
                                metricView === 'traffic' ? 'bg-white text-stone-900 shadow-xs text-brand-600' : 'hover:text-stone-900'
                            }`}
                        >
                            Clicks & Imp
                        </button>
                    </div>

                    {/* Date Range Selector */}
                    {gscToken && (
                        <select 
                            value={dateRange} 
                            onChange={(e) => handleDateRangeChange(Number(e.target.value))}
                            className="bg-stone-50 border border-stone-200 text-stone-700 text-xs font-semibold rounded-xl px-2.5 py-1.5 focus:outline-none"
                        >
                            <option value={7}>7 ngày qua</option>
                            <option value={28}>28 ngày qua</option>
                            <option value={90}>3 tháng qua</option>
                        </select>
                    )}

                    {/* Refresh Button */}
                    <button 
                        onClick={handleRefresh} 
                        disabled={loadingGsc}
                        className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-xl transition-colors border border-stone-200/60"
                        title="Làm mới dữ liệu Search Console"
                    >
                        <RefreshCw size={16} className={loadingGsc ? 'animate-spin text-brand-600' : ''}/>
                    </button>

                    {/* Detailed view button */}
                    <button 
                        onClick={() => setView(View.GSC_ANALYTICS)}
                        className="btn-primary text-xs px-3.5 py-2 rounded-xl flex items-center gap-1 font-bold shadow-xs hover:shadow-md"
                        title="Mở toàn màn hình GSC Matrix"
                    >
                        <span>GSC Matrix</span>
                        <ArrowUpRight size={14}/>
                    </button>
                </div>
            </div>

            {/* 4 Metric Badges Row */}
            <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-3.5 my-6">
                <div className="bg-stone-50/80 border border-stone-200/70 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <MousePointer size={18}/>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Tổng Clicks</div>
                        <div className="text-xl font-extrabold text-stone-900">
                            {hasData ? (gscToken ? totalClicks.toLocaleString() : '1,420') : '0'}
                        </div>
                    </div>
                </div>

                <div className="bg-stone-50/80 border border-stone-200/70 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                        <Eye size={18}/>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Impressions</div>
                        <div className="text-xl font-extrabold text-stone-900">
                            {hasData ? (gscToken ? totalImpressions.toLocaleString() : '38.5K') : '0'}
                        </div>
                    </div>
                </div>

                <div className="bg-stone-50/80 border border-stone-200/70 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <Zap size={18}/>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">CTR Trung bình</div>
                        <div className="text-xl font-extrabold text-emerald-700">
                            {hasData ? (gscToken ? `${avgCtr.toFixed(2)}%` : '3.69%') : '0%'}
                        </div>
                    </div>
                </div>

                <div className="bg-stone-50/80 border border-stone-200/70 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                        <MapPin size={18}/>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Vị trí TB (Avg Rank)</div>
                        <div className="text-xl font-extrabold text-amber-700">
                            {hasData ? (gscToken ? `#${avgPosition.toFixed(1)}` : '#6.2') : '-'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Visual Chart Area */}
            <div className="relative z-10 bg-gradient-to-b from-stone-50/70 to-white rounded-2xl border border-stone-200/80 p-5 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-brand-500"></span>
                        <h4 className="text-xs font-extrabold uppercase tracking-wider text-stone-800">
                            {metricView === 'ranking' 
                                ? 'Xu hướng Vị trí Thứ hạng Top 10 Từ khóa (SERP Rank Trend)' 
                                : 'Lưu lượng Truy cập: Clicks & Hiển thị Top 10 Từ khóa'}
                        </h4>
                    </div>
                    <span className="text-[11px] font-medium text-stone-500">
                        {metricView === 'ranking' 
                            ? 'Vị trí càng nhỏ (#1 - #10) = Thứ hạng càng cao' 
                            : 'Phân bổ Clicks và Impressions trên Google'}
                    </span>
                </div>

                {/* Chart Component */}
                <div className="h-64 w-full">
                    {hasData ? (
                        <ResponsiveContainer width="100%" height="100%">
                            {metricView === 'ranking' ? (
                                <AreaChart data={chartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="rankGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4"/>
                                    <XAxis 
                                        dataKey="keyword" 
                                        stroke="#78716c" 
                                        fontSize={11} 
                                        fontWeight="600"
                                        tickLine={false}
                                        axisLine={{ stroke: '#e7e5e4' }}
                                    />
                                    <YAxis 
                                        reversed 
                                        domain={[1, 50]} 
                                        ticks={[1, 5, 10, 20, 30, 50]}
                                        stroke="#78716c" 
                                        fontSize={11} 
                                        fontWeight="600"
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(v) => `#${v}`}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            borderRadius: '14px', 
                                            border: '1px solid #e7e5e4', 
                                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                                            padding: '10px 14px',
                                            backgroundColor: '#ffffff'
                                        }}
                                        formatter={(val: any, name: any, item: any) => [
                                            `Hạng #${item?.payload?.position || val}`, 
                                            'Vị trí Google'
                                        ]}
                                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullKeyword || label}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="position" 
                                        name="Thứ hạng" 
                                        stroke="#f59e0b" 
                                        strokeWidth={3} 
                                        fillOpacity={1} 
                                        fill="url(#rankGradient)"
                                        dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }}
                                        activeDot={{ r: 6, stroke: '#d97706', strokeWidth: 2 }}
                                    />
                                </AreaChart>
                            ) : (
                                <BarChart data={chartData} margin={{ top: 10, right: 15, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4"/>
                                    <XAxis 
                                        dataKey="keyword" 
                                        stroke="#78716c" 
                                        fontSize={11} 
                                        fontWeight="600"
                                        tickLine={false}
                                        axisLine={{ stroke: '#e7e5e4' }}
                                    />
                                    <YAxis 
                                        yAxisId="left" 
                                        stroke="#3b82f6" 
                                        fontSize={11} 
                                        fontWeight="bold"
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis 
                                        yAxisId="right" 
                                        orientation="right" 
                                        stroke="#a855f7" 
                                        fontSize={11} 
                                        fontWeight="bold"
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            borderRadius: '14px', 
                                            border: '1px solid #e7e5e4', 
                                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                                            padding: '10px 14px',
                                            backgroundColor: '#ffffff'
                                        }}
                                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullKeyword || label}
                                    />
                                    <Bar yAxisId="left" dataKey="clicks" name="Clicks" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                                    <Bar yAxisId="right" dataKey="impressions" name="Impressions" fill="#a855f7" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-stone-400">
                            <TrendingUp size={36} className="opacity-25 mb-2"/>
                            <p className="text-xs font-bold">Chưa có dữ liệu từ khóa xếp hạng.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Status / OAuth Connect Callout */}
            <div className="relative z-10 mt-6 pt-4 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 text-stone-500">
                    <ShieldCheck size={16} className="text-emerald-500 shrink-0"/>
                    {gscToken ? (
                        <span>
                            Đã xác thực Google OAuth. Đang đồng bộ số liệu Search Console cho <strong className="text-stone-800">{selectedSite}</strong>.
                        </span>
                    ) : (
                        <span>
                            Chế độ Preview dữ liệu mẫu & Local Rank Tracker. Kết nối tài khoản Google để đồng bộ tự động.
                        </span>
                    )}
                </div>

                {!gscToken ? (
                    <button 
                        onClick={handleConnectGsc}
                        className="flex items-center gap-2 bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-xl font-bold text-xs transition-all shadow-sm active:scale-95"
                    >
                        <img src="https://www.google.com/favicon.ico" className="w-4 h-4 bg-white rounded-full p-0.5" alt="G"/>
                        <span>Kết nối Google Search Console</span>
                    </button>
                ) : (
                    <button 
                        onClick={() => setView(View.GSC_ANALYTICS)}
                        className="text-brand-600 hover:text-brand-700 font-bold flex items-center gap-1 hover:underline"
                    >
                        <span>Xem chi tiết Insight AI & Dự báo tăng trưởng</span>
                        <ChevronRight size={14}/>
                    </button>
                )}
            </div>
        </div>
    );
};

export default SeoPerformanceSummary;
